import { forwardRef, useEffect, useState, useImperativeHandle } from "react"
import { Button } from "components/ui/button"
import { toast } from "components/ui/toaster/use-toast"
import { ToastAction } from "components/ui/toaster/toast"
import axiosInstance from "axiosInstance"
import { Input } from "components/ui/input"
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs"
import { AlertCircle, Save, Edit2, Clock, Scissors, MergeIcon } from "lucide-react"
import { Alert, AlertDescription } from "components/ui/alert"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select"

function TextEditor({ videoID, setCurrentStep }, ref) {
  const initialData = [
    {
      chunk: "This is dummy data",
      keyword: "Dummy Data",
      word_level_timestamps: [
        {
          end: 0.27,
          word: "This",
          start: 0.07,
        },
        {
          end: 0.43,
          word: "is",
          start: 0.27,
        },
        {
          end: 0.47,
          word: "dummy",
          start: 0.43,
        },
        {
          end: 0.71,
          word: "data",
          start: 0.47,
        },
      ],
    },
  ]

  const [chunks, setChunks] = useState(initialData)
  const [editingIndex, setEditingIndex] = useState(null)
  const [saveStatus, setSaveStatus] = useState(null)
  const [activeTab, setActiveTab] = useState("editor")
  const [splitWordIndex, setSplitWordIndex] = useState(null)
  const [splitChunkIndex, setSplitChunkIndex] = useState(null)
  const [showSplitDialog, setShowSplitDialog] = useState(false)
  const [showMergeDialog, setShowMergeDialog] = useState(false)
  const [mergeSourceIndex, setMergeSourceIndex] = useState(null)
  const [mergeTargetIndex, setMergeTargetIndex] = useState(null)
  const [editingWordData, setEditingWordData] = useState({ chunkIndex: null, wordIndex: null, word: "" })

  // Check if videoID is present
  useEffect(() => {
    console.log("videoID", videoID)
    if (videoID) {
      fetchChunks()
    } else {
      alert("Please upload the video first")
      setCurrentStep(0)
    }
  }, [videoID])

  // Function to fetch chunks from the backend
  const fetchChunks = async () => {
    console.log("videoID", videoID)
    const config = {
      headers: { "content-type": "application/json" },
    }

    const response = await axiosInstance.get(
      process.env.REACT_APP_BACKEND_URL + `/microservices/marketier/get_chunks/${videoID}`,
      config,
    )

    setChunks(response.data)
  }

  // Function to update chunks in the backend
  const updateChunks = async () => {
    console.log("videoID", videoID)
    const config = {
      headers: { "content-type": "application/json" },
    }

    const response = await axiosInstance.patch(
      process.env.REACT_APP_BACKEND_URL + `/microservices/marketier/update_chunks/${videoID}`,
      chunks,
      config,
    )
  }

  // Function to handle editing a chunk
  const handleEdit = (index) => {
    setEditingIndex(index)
  }

  // Function to handle saving changes to a chunk
  const handleSave = async (index) => {
    setEditingIndex(null)
    await updateChunks()
    setSaveStatus("Changes saved successfully!")
    toast({
      title: "Changes Saved",
      description: "Check the console for the JSON output.",
      action: <ToastAction altText="Close">Close</ToastAction>,
    })
    // Clear the status message after 3 seconds
    setTimeout(() => {
      setSaveStatus(null)
    }, 3000)
  }

  // Expose handleSave through the ref
  useImperativeHandle(ref, () => ({
    handleSave: handleSave,
  }))

  // Function to update keyword
  const updateKeyword = (index, newKeyword) => {
    const updatedChunks = [...chunks]
    updatedChunks[index].keyword = newKeyword
    setChunks(updatedChunks)
  }

  // Function to update a word in the timestamps and reconstruct the chunk text
  const updateWord = (chunkIndex, wordIndex, newWord) => {
    const updatedChunks = [...chunks]
    updatedChunks[chunkIndex].word_level_timestamps[wordIndex].word = newWord

    // Reconstruct the chunk text from the word timestamps
    updatedChunks[chunkIndex].chunk = updatedChunks[chunkIndex].word_level_timestamps
      .map((timestamp) => timestamp.word)
      .join(" ")

    setChunks(updatedChunks)
  }

  // Function to handle editing a word
  const startEditingWord = (chunkIndex, wordIndex, word) => {
    setEditingWordData({
      chunkIndex,
      wordIndex,
      word,
    })
  }

  // Function to save edited word
  const saveEditedWord = () => {
    const { chunkIndex, wordIndex, word } = editingWordData
    if (chunkIndex !== null && wordIndex !== null) {
      updateWord(chunkIndex, wordIndex, word)
      setEditingWordData({ chunkIndex: null, wordIndex: null, word: "" })
    }
  }

  // Function to cancel word editing
  const cancelEditingWord = () => {
    setEditingWordData({ chunkIndex: null, wordIndex: null, word: "" })
  }

  // Function to export JSON
  const exportJson = () => {
    const jsonString = JSON.stringify(chunks, null, 2)
    const blob = new Blob([jsonString], { type: "application/json" })
    const url = URL.createObjectURL(blob)

    const a = document.createElement("a")
    a.href = url
    a.download = "chunks.json"
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // Function to handle file import
  const handleFileImport = (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const content = e.target?.result
        const parsedData = JSON.parse(content)
        setChunks(parsedData)
        setSaveStatus("JSON imported successfully!")

        // Clear the status message after 3 seconds
        setTimeout(() => {
          setSaveStatus(null)
        }, 3000)
      } catch (error) {
        setSaveStatus("Error importing JSON. Please check the file format.")

        // Clear the status message after 3 seconds
        setTimeout(() => {
          setSaveStatus(null)
        }, 3000)
      }
    }
    reader.readAsText(file)
  }

  // Function to open split dialog
  const openSplitDialog = (chunkIndex) => {
    setSplitChunkIndex(chunkIndex)
    setSplitWordIndex(null)
    setShowSplitDialog(true)
  }

  // Function to split a chunk
  const splitChunk = () => {
    if (splitChunkIndex === null || splitWordIndex === null) return

    const chunkToSplit = chunks[splitChunkIndex]
    const splitPoint = Number.parseInt(splitWordIndex)

    // Create two new chunks
    const firstChunkTimestamps = chunkToSplit.word_level_timestamps.slice(0, splitPoint + 1)
    const secondChunkTimestamps = chunkToSplit.word_level_timestamps.slice(splitPoint + 1)

    const firstChunkText = firstChunkTimestamps.map((ts) => ts.word).join(" ")
    const secondChunkText = secondChunkTimestamps.map((ts) => ts.word).join(" ")

    const newChunks = [...chunks]

    // Replace the original chunk with the first part
    newChunks[splitChunkIndex] = {
      chunk: firstChunkText,
      keyword: chunkToSplit.keyword,
      word_level_timestamps: firstChunkTimestamps,
    }

    // Insert the second part after the original chunk
    newChunks.splice(splitChunkIndex + 1, 0, {
      chunk: secondChunkText,
      keyword: chunkToSplit.keyword + " (split)",
      word_level_timestamps: secondChunkTimestamps,
    })

    setChunks(newChunks)
    setShowSplitDialog(false)
    setSaveStatus("Chunk split successfully!")

    setTimeout(() => {
      setSaveStatus(null)
    }, 3000)
  }

  // Function to open merge dialog
  const openMergeDialog = () => {
    setMergeSourceIndex(null)
    setMergeTargetIndex(null)
    setShowMergeDialog(true)
  }

  // Function to merge chunks
  const mergeChunks = () => {
    if (mergeSourceIndex === null || mergeTargetIndex === null) return

    const sourceIndex = Number.parseInt(mergeSourceIndex)
    const targetIndex = Number.parseInt(mergeTargetIndex)

    // Ensure source comes before target for chronological order
    const firstIndex = Math.min(sourceIndex, targetIndex)
    const secondIndex = Math.max(sourceIndex, targetIndex)

    const firstChunk = chunks[firstIndex]
    const secondChunk = chunks[secondIndex]

    // Combine timestamps in chronological order
    const combinedTimestamps = [...firstChunk.word_level_timestamps, ...secondChunk.word_level_timestamps]

    // Create merged chunk text
    const mergedText = combinedTimestamps.map((ts) => ts.word).join(" ")

    const newChunks = [...chunks]

    // Create the merged chunk
    const mergedChunk = {
      chunk: mergedText,
      keyword: firstChunk.keyword + " + " + secondChunk.keyword,
      word_level_timestamps: combinedTimestamps,
    }

    // Replace the first chunk with the merged chunk and remove the second chunk
    newChunks[firstIndex] = mergedChunk
    newChunks.splice(secondIndex, 1)

    setChunks(newChunks)
    setShowMergeDialog(false)
    setSaveStatus("Chunks merged successfully!")

    setTimeout(() => {
      setSaveStatus(null)
    }, 3000)
  }

  return (
    <div className="container mx-auto p-2 md:p-4 max-w-4xl">
      {saveStatus && (
        <Alert className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{saveStatus}</AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col mb-6">
        <TabsList className="flex flex-col md:flex-row mb-4">
          <div>
            <TabsTrigger value="editor">Editor</TabsTrigger>
            <TabsTrigger value="preview">JSON Preview</TabsTrigger>
          </div>
          <Button variant="outline" onClick={openMergeDialog}>
            <MergeIcon className="h-4 w-4 mr-2" />
            Merge Chunks
          </Button>
        </TabsList>
        <TabsContent value="editor">
          <div className="space-y-6">
            {chunks &&
              chunks.map((chunk, index) => (
                <Card key={index} className="overflow-hidden">
                  <CardHeader className="bg-blue-100">
                    <CardTitle className="flex flex-col md:flex-row justify-between items-center">
                      <span>Chunk {index + 1}</span>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => openSplitDialog(index)}>
                          <Scissors className="h-4 w-4 mr-2" />
                          Split
                        </Button>
                        {editingIndex === index ? (
                          <Button size="sm" onClick={() => handleSave(index)}>
                            <Save className="h-4 w-4 mr-2" />
                            Save
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => handleEdit(index)}>
                            <Edit2 className="h-4 w-4 mr-2" />
                            Edit
                          </Button>
                        )}
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <div>
                        <label htmlFor={`chunk-${index}`} className="block text-sm font-medium mb-2">
                          Chunk Text (Generated from Words)
                        </label>
                        <div className="p-3 bg-blue-100 rounded-md">{chunk.chunk}</div>
                      </div>
                      <div>
                        <label htmlFor={`keyword-${index}`} className="block text-sm font-medium mb-2">
                          Keyword
                        </label>
                        {editingIndex === index ? (
                          <Input
                            id={`keyword-${index}`}
                            value={chunk.keyword}
                            onChange={(e) => updateKeyword(index, e.target.value)}
                            className="w-full"
                          />
                        ) : (
                          <div className="p-3 bg-blue-100 rounded-md">{chunk.keyword}</div>
                        )}
                      </div>
                      <div>
                        <h4 className="text-sm font-medium mb-2">Word Timestamps (Click to Edit)</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {chunk.word_level_timestamps.map((timestamp, wordIndex) => (
                            <div
                              key={wordIndex}
                              className="flex items-center p-2 bg-blue-100/50 rounded-md cursor-pointer hover:bg-blue-200/50 transition-colors"
                              onClick={() => startEditingWord(index, wordIndex, timestamp.word)}
                            >
                              <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
                              <span className="font-medium mr-2">{timestamp.word}:</span>
                              <span className="text-sm text-muted-foreground">
                                {timestamp.start.toFixed(2)}s - {timestamp.end.toFixed(2)}s
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
        </TabsContent>
        <TabsContent value="preview">
          <Card>
            <CardHeader>
              <CardTitle>JSON Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="bg-blue-100 p-2 md:p-4 rounded-md overflow-auto max-h-[300px] md:max-h-[500px] text-xs md:text-sm">
                {JSON.stringify(chunks, null, 2)}
              </pre>
            </CardContent>
            {/* <CardFooter>
              <Button onClick={exportJson}>Export JSON</Button>
            </CardFooter> */}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Word Edit Dialog */}
      {editingWordData.chunkIndex !== null && (
        <Dialog open={true} onOpenChange={(open) => !open && cancelEditingWord()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Word</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Input
                value={editingWordData.word}
                onChange={(e) => setEditingWordData({ ...editingWordData, word: e.target.value })}
                className="w-full"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={cancelEditingWord}>
                Cancel
              </Button>
              <Button onClick={saveEditedWord}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Split Dialog */}
      <Dialog open={showSplitDialog} onOpenChange={setShowSplitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Split Chunk</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="mb-4">Select the word after which to split the chunk:</p>
            {splitChunkIndex !== null && (
              <Select onValueChange={setSplitWordIndex}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a word" />
                </SelectTrigger>
                <SelectContent>
                  {chunks[splitChunkIndex].word_level_timestamps.map((timestamp, index) => (
                    <SelectItem key={index} value={index.toString()}>
                      {timestamp.word} ({timestamp.end.toFixed(2)}s)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSplitDialog(false)}>
              Cancel
            </Button>
            <Button onClick={splitChunk} disabled={splitWordIndex === null}>
              Split
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Dialog */}
      <Dialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Merge Chunks</DialogTitle>
          </DialogHeader>
          <div className="py-2 md:py-4 space-y-2 md:space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">First Chunk</label>
              <Select onValueChange={setMergeSourceIndex}>
                <SelectTrigger>
                  <SelectValue placeholder="Select first chunk" />
                </SelectTrigger>
                <SelectContent>
                  {chunks &&
                    chunks.map((chunk, index) => (
                      <SelectItem key={index} value={index.toString()}>
                        Chunk {index + 1}: {chunk.chunk.substring(0, 20)}...
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Second Chunk</label>
              <Select onValueChange={setMergeTargetIndex}>
                <SelectTrigger>
                  <SelectValue placeholder="Select second chunk" />
                </SelectTrigger>
                <SelectContent>
                  {chunks &&
                    chunks.map((chunk, index) => (
                      <SelectItem key={index} value={index.toString()} disabled={index.toString() === mergeSourceIndex}>
                        Chunk {index + 1}: {chunk.chunk.substring(0, 20)}...
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMergeDialog(false)}>
              Cancel
            </Button>
            <Button onClick={mergeChunks} disabled={mergeSourceIndex === null || mergeTargetIndex === null}>
              Merge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="mt-4 space-x-2 flex items-center justify-center">
        <Button
          onClick={handleSave}
          variant="default"
          className="text-white bg-green-500 rounded-full hover:bg-green-600"
        >
          Save Changes
        </Button>
      </div>
    </div>
  )
}

export default forwardRef(TextEditor)